// StoreCatalog.js
import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { CatalogDataService } from '../../Services/CatalogsDataService';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import noImage from '../../Assets/img/catalogs/no-store.png';
import './StoreCatalog.scss';
import { InteractionType } from '@azure/msal-browser';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { useNavigate } from 'react-router-dom';

const StoreCatalog = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [stores, setStores] = useState([]);
    const [search, setSearch] = useState('');

    // Filter selections
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedPostalCodes, setSelectedPostalCodes] = useState([]);

    // (Assume you load available filter options from API similarly.)
    const [countryOptions, setCountryOptions] = useState([]);
    const [regionOptions, setRegionOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [postalCodeOptions, setPostalCodeOptions] = useState([]);

    // Sorting state (for example, sort by name or address)
    const [sortBy, setSortBy] = useState("name");
    const [sortAsc, setSortAsc] = useState(true);

    // Pagination states
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const pageCount = Math.ceil(totalCount / pageSize);

    const navigate = useNavigate();

    const fetchStores = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const tokenResponse = await instance.acquireTokenSilent(request);
            const params = {
                search,
                // Frontend arrays (assuming using qs serializer to send as repeat)
                countries: selectedCountries.map(opt => opt.value),
                regions: selectedRegions.map(opt => opt.value),
                cities: selectedCities.map(opt => opt.value),
                postalCodes: selectedPostalCodes.map(opt => opt.value),
                sortBy,
                sortAsc,
                pageIndex,
                pageSize
            };
            const response = await CatalogDataService.getStores(params, tokenResponse.accessToken);
            if (response && response.data) {
                setStores(response.data.items);
                setTotalCount(response.data.totalCount);
            }
        } catch (error) {
            console.error('Error fetching stores:', error);
        }
    };

    // Fetch available filter options (assuming you have an endpoint for this)
    const fetchStoreFilters = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const tokenResponse = await instance.acquireTokenSilent(request);
            const response = await CatalogDataService.getStoreFilterOptions(tokenResponse.accessToken);
            if (response && response.data) {
                const { countries, regions, cities, postalCodes } = response.data;
                setCountryOptions(countries.map(item => ({ value: item, label: item })));
                setRegionOptions(regions.map(item => ({ value: item, label: item })));
                setCityOptions(cities.map(item => ({ value: item, label: item })));
                setPostalCodeOptions(postalCodes.map(item => ({ value: item, label: item })));
            }
        } catch (error) {
            console.error('Error fetching store filter options:', error);
        }
    };

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchStoreFilters();
            fetchStores();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProgress, accounts]);

    // Re-fetch when any filter/sort/search or pagination value changes
    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchStores();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, selectedCountries, selectedRegions, selectedCities, selectedPostalCodes, sortBy, sortAsc, pageIndex, pageSize]);

    return (
        <Container className="store-catalog">
            {/* Header: Filters on the left, Search bar on the right */}
            <div className="catalog-header">
                <div className="filters">
                    <Select
                        isMulti
                        options={countryOptions}
                        placeholder="Filter by Country"
                        value={selectedCountries}
                        onChange={selected => setSelectedCountries(selected || [])}
                    />
                    <Select
                        isMulti
                        options={regionOptions}
                        placeholder="Filter by Region"
                        value={selectedRegions}
                        onChange={selected => setSelectedRegions(selected || [])}
                    />
                    <Select
                        isMulti
                        options={cityOptions}
                        placeholder="Filter by City"
                        value={selectedCities}
                        onChange={selected => setSelectedCities(selected || [])}
                    />
                    <Select
                        isMulti
                        options={postalCodeOptions}
                        placeholder="Filter by Postal Code"
                        value={selectedPostalCodes}
                        onChange={selected => setSelectedPostalCodes(selected || [])}
                    />
                </div>
                <div className="search-bar ms-4">
                    <FontAwesomeIcon icon={faSearch} />
                    <input 
                        type="text" 
                        placeholder="search" 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Store Cards Grid */}
            <div className="catalog-grid">
                {stores.map((store) => (
                    <Card key={store.storeSK} className="catalog-card" onClick={() => navigate(`/store-catalog/${store.storeSK}`)}>
                        <Card.Img
                            variant="top"
                            src={store.imageUrl || noImage}
                            onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = noImage;
                            }}
                        />
                        <Card.Body>
                            <Card.Title className="store-title">{store.storeName}</Card.Title>
                            <Card.Text className="store-address">{store.fullAddress}</Card.Text>
                        </Card.Body>
                    </Card>
                ))}
            </div>

            {/* Pagination Controls */}
            <div className="d-flex align-items-center gx-2 mt-3">
                <nav>
                    <ul className="pagination m-0">
                        <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="First" onClick={(e) => { e.preventDefault(); setPageIndex(0); }}>
                                <span aria-hidden="true">&laquo;</span>
                            </a>
                        </li>
                        <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Previous" onClick={(e) => { e.preventDefault(); setPageIndex(pageIndex - 1); }}>
                                <span aria-hidden="true">&lt;</span>
                            </a>
                        </li>
                        <li className={`page-item ${pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Next" onClick={(e) => { e.preventDefault(); setPageIndex(pageIndex + 1); }}>
                                <span aria-hidden="true">&gt;</span>
                            </a>
                        </li>
                        <li className={`page-item ${pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Last" onClick={(e) => { e.preventDefault(); setPageIndex(pageCount - 1); }}>
                                <span aria-hidden="true">&raquo;</span>
                            </a>
                        </li>
                    </ul>
                </nav>
                <span className="d-flex align-items-center gx-1 ms-2">
                    <div>Page</div>
                    <strong>
                        {pageIndex + 1} of {pageCount}
                    </strong>
                </span>
                <span className="d-flex align-items-center gx-1 ms-2">
                    | Go to page:
                    <input
                        type="number"
                        defaultValue={pageIndex + 1}
                        onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            setPageIndex(page);
                        }}
                        className="form-control w-auto ms-1"
                    />
                </span>
                <select
                    value={pageSize}
                    onChange={e => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to first page when page size changes
                    }}
                    className="form-select w-auto ms-2"
                >
                    {[10, 20, 30, 40, 50].map(size => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </select>
            </div>
        </Container>
    );
};

const StoreCatalogPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Store Catalog">
                <StoreCatalog />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default StoreCatalogPage;
